import styled, { css } from "styled-components";
import type { DefaultTheme } from "styled-components";
import {
  BranchSemanticColour,
  typographyCSS,
} from "@ifgengineering/design-tokens/legacy";
import { branch } from "@styles/branchColour";
import { onShortDesktop } from "@components/onboarding/Layout/StepLayout.styled";
import type { JourneyStageState, SubStepState } from "./journey";

/** The narrowest screen that fits the rail beside the task. */
const RAIL_MIN_WIDTH = 1024;
const RAIL_WIDTH = 304;
const WIDE_RAIL_WIDTH = 336;
const WIDE_MIN_WIDTH = 1280;
/** The measure of the task column; a layout width, not spacing. */
export const TASK_MAX_WIDTH = 600;
const TOP_BAR_HEIGHT = 64;

const withRail = `@media (min-width: ${RAIL_MIN_WIDTH}px)`;
const withWideRail = `@media (min-width: ${WIDE_MIN_WIDTH}px)`;
export const withoutRail = `@media (max-width: ${RAIL_MIN_WIDTH - 1}px)`;
const canHover = "@media (hover: hover) and (pointer: fine)";

type Themed = { theme: DefaultTheme };

export const focusRing = css`
  &:focus-visible {
    outline: ${({ theme }) => theme.stroke.focus}px solid
      ${({ theme }) => branch(theme, "border/focus")};
    outline-offset: ${({ theme }) => theme.stroke.focus}px;
  }
`;

const hairline = ({ theme }: Themed): string =>
  `${theme.stroke.md}px solid ${branch(theme, "border/subtle")}`;

/* Top bar */

export const TopBar = styled.header`
  box-sizing: border-box;
  height: ${TOP_BAR_HEIGHT}px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.card.gap}px;
  padding: 0 ${({ theme }) => theme.space.section.gap}px;
  border-bottom: ${hairline};
  background: ${({ theme }) => branch(theme, "background/page")};
`;

export const Logo = styled.img`
  display: block;
  height: ${({ theme }) => theme.size.icon.lg}px;
  width: auto;
`;

export const SaveExitButton = styled.button`
  ${typographyCSS("label.sm")}
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm}px;
  height: ${() => 40}px;
  padding: 0 ${({ theme }) => theme.space.card.inset}px;
  border-radius: ${({ theme }) => theme.radius.full}px;
  border: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) => branch(theme, "border/default")};
  background: none;
  color: ${({ theme }) => branch(theme, "text/primary")};
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.motion.duration.quick}ms
    ${({ theme }) => theme.motion.easing.standard};
  ${focusRing}

  ${canHover} {
    &:hover:not(:disabled) {
      border-color: ${({ theme }) => branch(theme, "border/strong")};
    }
  }

  &:disabled {
    cursor: progress;
    color: ${({ theme }) => branch(theme, "text/disabled")};
  }
`;

/* Deal summary */

export const DealBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md}px;
  min-width: 0;
`;

export const DealMedia = styled.div`
  flex: none;
  overflow: hidden;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  width: ${({ theme }) => theme.size.control.md}px;
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  border: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) => branch(theme, "border/subtle")};
  background: ${({ theme }) => branch(theme, "background/inset")};

  & > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const DealInitial = styled.span`
  ${typographyCSS("label.sm-semibold")}
  color: ${({ theme }) => branch(theme, "text/secondary")};
`;

export const DealText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const DealKicker = styled.span`
  ${typographyCSS("caption.sm-medium")}
  color: ${({ theme }) => branch(theme, "text/tertiary")};
`;

const dealName = css`
  ${typographyCSS("label.sm-semibold")}
  color: ${({ theme }) => branch(theme, "text/primary")};
  text-wrap: balance;
`;

export const DealName = styled.span`
  ${dealName}
`;

export const DealNameLink = styled.a`
  && {
    ${dealName}
  }
  align-self: flex-start;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.xs}px;
  ${focusRing}

  ${canHover} {
    &:hover {
      text-decoration: underline;
      text-underline-offset: ${({ theme }) => theme.space["3xs"] * 2}px;
    }
  }
`;

export const DealMeta = styled.span`
  ${typographyCSS("body.sm")}
  color: ${({ theme }) => branch(theme, "text/secondary")};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/* Journey list */

const markerSize = ({ theme }: Themed): number => theme.size.icon.md;

export const JourneyNav = styled.nav``;

export const Stages = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

/** Each stage draws the connector down to the next one; a complete stage colours it. */
export const Stage = styled.li<{ $complete: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: ${markerSize}px minmax(0, 1fr);
  column-gap: ${({ theme }) => theme.space.md}px;
  padding-bottom: ${({ theme }) => theme.space.section.gap}px;

  &:not(:last-child)::before {
    content: "";
    position: absolute;
    top: ${({ theme }) => markerSize({ theme }) + theme.space["2xs"]}px;
    bottom: ${({ theme }) => theme.space["2xs"]}px;
    left: ${({ theme }) => (markerSize({ theme }) - theme.stroke.md) / 2}px;
    width: ${({ theme }) => theme.stroke.md}px;
    background: ${({ theme, $complete }) =>
      $complete
        ? branch(theme, "action/primary/background")
        : branch(theme, "border/subtle")};
  }
`;

export type MarkerLook = "done" | "current" | "locked" | "review" | "todo";

const MARKER_LOOK: Record<MarkerLook, (theme: DefaultTheme) => string> = {
  done: (theme) => `
    background: ${branch(theme, "action/primary/background")};
    border-color: ${branch(theme, "action/primary/background")};
    color: ${branch(theme, "action/primary/icon")};
  `,
  current: (theme) => `
    border-width: ${theme.stroke.selected}px;
    border-color: ${branch(theme, "action/primary/background")};
    color: ${branch(theme, "text/primary")};
  `,
  locked: (theme) => `
    border-color: ${branch(theme, "border/default")};
    color: ${
      theme.mode === "light" ? "#B7C1C8" : branch(theme, "text/tertiary")
    };
  `,
  review: (theme) => `
    border-color: ${branch(theme, "border/strong")};
    color: ${branch(theme, "icon/secondary")};
  `,
  todo: (theme) => `
    border-color: ${branch(theme, "border/default")};
    color: ${branch(theme, "text/tertiary")};
  `,
};

export const Marker = styled.span<{ $look: MarkerLook }>`
  ${typographyCSS("caption.sm-medium")}
  font-weight: 600;
  grid-column: 1;
  box-sizing: border-box;
  width: ${markerSize}px;
  height: ${markerSize}px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.radius.full}px;
  border: ${({ theme }) => theme.stroke.md}px solid;
  background: ${({ theme }) => branch(theme, "background/page")};
  font-variant-numeric: tabular-nums;
  ${({ theme, $look }) => MARKER_LOOK[$look](theme)}
`;

export const StageBody = styled.div`
  grid-column: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const StageHead = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.md}px;
  min-height: ${markerSize}px;
`;

/** Stages other than the one on show; that one always reads as primary. */
const STAGE_LABEL_COLOUR: Record<JourneyStageState, BranchSemanticColour> = {
  done: "text/secondary",
  current: "text/secondary",
  review: "text/secondary",
  locked: "text/tertiary",
  todo: "text/tertiary",
};

export const StageLabel = styled.span<{
  $state: JourneyStageState;
  $isCurrent: boolean;
}>`
  ${({ $isCurrent }) =>
    typographyCSS($isCurrent ? "label.sm-semibold" : "label.sm")}
  color: ${({ theme, $state, $isCurrent }) =>
    $isCurrent
      ? branch(theme, "text/primary")
      : branch(theme, STAGE_LABEL_COLOUR[$state])};
`;

/** A complete stage the investor can open again: quiet like its neighbours until hovered or focused. */
export const StageLink = styled.a`
  && {
    ${typographyCSS("label.sm")}
    color: ${({ theme }) => branch(theme, "text/secondary")};
    text-decoration: none;
  }
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space["3xs"]}px;
  min-width: 0;
  border-radius: ${({ theme }) => theme.radius.xs}px;
  transition: color ${({ theme }) => theme.motion.duration.quick}ms
    ${({ theme }) => theme.motion.easing.standard};
  ${focusRing}

  & > svg {
    flex: none;
    color: ${({ theme }) =>
      theme.mode === "light" ? "#B7C1C8" : branch(theme, "text/tertiary")};
  }

  ${canHover} {
    &&:hover {
      color: ${({ theme }) => branch(theme, "text/primary")};
      text-decoration: underline;
      text-underline-offset: ${({ theme }) => theme.space["3xs"] * 2}px;
    }
  }
`;

export const StageStatus = styled.span`
  ${typographyCSS("caption.sm")}
  flex: none;
  color: ${({ theme }) => branch(theme, "text/tertiary")};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const StageDetail = styled.span`
  ${typographyCSS("body.sm")}
  color: ${({ theme }) => branch(theme, "text/secondary")};
`;

export const SubSteps = styled.ol`
  list-style: none;
  margin: ${({ theme }) => theme.space.md}px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const SUB_STEP_COLOUR: Record<SubStepState, BranchSemanticColour> = {
  current: "text/primary",
  done: "text/secondary",
  todo: "text/tertiary",
};

const subStepBody = css<{ $state: SubStepState }>`
  ${({ $state }) =>
    typographyCSS($state === "current" ? "label.sm-semibold" : "body.sm")}
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.sm}px;
  min-height: ${() => 40}px;
  padding: 0 0 0 ${({ theme }) => theme.space.md + theme.stroke.selected}px;
  border: 0;
  color: ${({ theme, $state }) => branch(theme, SUB_STEP_COLOUR[$state])};
  text-align: left;

  ${({ theme, $state }) =>
    $state === "current" &&
    css`
      padding-left: ${theme.space.md}px;
      border-left: ${theme.stroke.selected}px solid
        ${branch(theme, "action/primary/background")};
    `}
`;

export const SubStepText = styled.span<{ $state: SubStepState }>`
  ${subStepBody}
`;

export const SubStepButton = styled.button<{ $state: SubStepState }>`
  ${subStepBody}
  background: none;
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.duration.quick}ms
    ${({ theme }) => theme.motion.easing.standard};
  ${focusRing}

  ${canHover} {
    &:hover {
      color: ${({ theme }) => branch(theme, "text/primary")};
      text-decoration: underline;
      text-underline-offset: ${({ theme }) => theme.space["3xs"] * 2}px;
    }
  }
`;

export const SubStepTick = styled.span`
  flex: none;
  display: inline-flex;
  color: ${({ theme }) => branch(theme, "action/primary/background")};
`;

export const JourneyNote = styled.p`
  ${typographyCSS("body.sm")}
  margin: 0;
  display: grid;
  grid-template-columns: ${markerSize}px minmax(0, 1fr);
  column-gap: ${({ theme }) => theme.space.md}px;
  align-items: start;
  color: ${({ theme }) => branch(theme, "text/secondary")};

  & > svg {
    justify-self: center;
    color: ${({ theme }) =>
      theme.mode === "light" ? "#B7C1C8" : branch(theme, "text/tertiary")};
  }
`;

/* Rail foot */

export const RailHelp = styled.p`
  ${typographyCSS("body.sm")}
  margin: auto 0 0;
  padding-top: ${({ theme }) => theme.space.card.gap}px;
  border-top: ${hairline};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space["3xs"]}px;
  color: ${({ theme }) => branch(theme, "text/tertiary")};
`;

const helpAction = css`
  ${typographyCSS("label.sm")}
  padding: 0;
  border: 0;
  background: none;
  color: ${({ theme }) => branch(theme, "text/link")};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.xs}px;
  cursor: pointer;
  ${focusRing}

  ${canHover} {
    &:hover {
      text-decoration: underline;
      text-underline-offset: ${({ theme }) => theme.space["3xs"] * 2}px;
    }
  }
`;

export const RailHelpLink = styled.a`
  && {
    ${helpAction}
  }
`;

export const RailHelpButton = styled.button`
  ${helpAction}
`;

/* Narrow progress bar with the rail as a sheet */

/** The widest screen that drops the stage markers and keeps just the words. */
const MARKERS_MIN_WIDTH = 480;

export const NarrowBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: ${hairline};
  background: ${({ theme }) => branch(theme, "background/page")};
`;

export const NarrowBarRow = styled.div`
  box-sizing: border-box;
  min-height: ${TOP_BAR_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.card.gap}px;
  padding: ${({ theme }) => `${theme.space.md}px ${theme.space.section.gap}px`};
`;

export const NarrowProgress = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.card.gap}px;
  min-width: 0;
`;

/** The rail's markers in a row, joined by the same connector. */
export const MarkerTrack = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: none;
  align-items: center;

  @media (min-width: ${MARKERS_MIN_WIDTH}px) {
    display: flex;
  }
`;

export const MarkerStop = styled.li<{ $complete: boolean }>`
  display: flex;
  align-items: center;

  &:not(:last-child)::after {
    content: "";
    width: ${({ theme }) => theme.space.md}px;
    height: ${({ theme }) => theme.stroke.md}px;
    background: ${({ theme, $complete }) =>
      $complete
        ? branch(theme, "action/primary/background")
        : branch(theme, "border/subtle")};
  }
`;

export const NarrowPosition = styled.p`
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: ${({ theme }) => theme.space.sm}px;
  min-width: 0;
`;

export const NarrowSection = styled.span`
  ${typographyCSS("label.sm-semibold")}
  color: ${({ theme }) => branch(theme, "text/primary")};
`;

export const NarrowCounter = styled.span`
  ${typographyCSS("label.sm")}
  color: ${({ theme }) => branch(theme, "text/tertiary")};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const DisclosureButton = styled(SaveExitButton)`
  & > svg {
    transition: transform ${({ theme }) => theme.motion.duration.quick}ms
      ${({ theme }) => theme.motion.easing.standard};
  }

  &[aria-expanded="true"] > svg {
    transform: rotate(180deg);
  }
`;

/** The rail's own blocks and spacing, laid over the task under the bar. */
export const DisclosurePanel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  box-sizing: border-box;
  max-height: calc(100vh - ${TOP_BAR_HEIGHT * 2}px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["4xl"]}px;
  padding: ${({ theme }) =>
    `${theme.space["4xl"]}px ${theme.space.section.gap}px ${theme.space.section.gap}px`};
  border-bottom: ${hairline};
  background: ${({ theme }) => branch(theme, "background/page")};

  & > * {
    max-width: ${WIDE_RAIL_WIDTH}px;
  }

  &[hidden] {
    display: none;
  }
`;

/* Frame */

export const Frame = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export const RailBody = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr);

  ${withRail} {
    grid-template-columns: ${RAIL_WIDTH}px minmax(0, 1fr);
  }

  ${withWideRail} {
    grid-template-columns: ${WIDE_RAIL_WIDTH}px minmax(0, 1fr);
  }
`;

export const Rail = styled.aside`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["4xl"]}px;
  padding: ${({ theme }) =>
    `${theme.space["4xl"]}px ${theme.space.section.gap}px ${theme.space.section.gap}px`};
  border-right: ${hairline};
  background: ${({ theme }) => branch(theme, "background/page")};

  ${withRail} {
    position: sticky;
    top: 0;
    align-self: start;
    height: calc(100vh - ${TOP_BAR_HEIGHT}px);
    overflow-y: auto;
  }

  ${withoutRail} {
    display: none;
  }
`;

export const OnlyBelowRail = styled.div`
  ${withRail} {
    display: none;
  }
`;

/** The task column, centred in the space beside the rail. */
export const TaskPane = styled.main`
  box-sizing: border-box;
  justify-self: center;
  width: 100%;
  max-width: ${({ theme }) => TASK_MAX_WIDTH + 2 * theme.space.section.gap}px;
  padding: ${({ theme }) =>
    `${theme.space["4xl"]}px ${theme.space.section.gap}px ${
      theme.space["4xl"] * 2
    }px`};
  display: flex;
  flex-direction: column;

  ${onShortDesktop} {
    padding-top: ${({ theme }) => theme.space.section.gap}px;
    padding-bottom: ${({ theme }) => theme.space.card.gap}px;
  }
`;
